import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// State
import { getIsAllConditionsCheckedSelector } from '../../../state/modules/stock/selectors';
import { addCartItem } from '../../../state/modules/cart/actions';
import {
  addReward,
  cancelRemovedIngredients,
  removeIngredient,
} from '../../../state/modules/stock/actions';

export default function useNewStockGoods({ initialNodeId, isReplace, onModalClose } = {}) {
  const dispatch = useDispatch();

  const allConditionsChecked = useSelector(getIsAllConditionsCheckedSelector);
  const isApplied = useSelector(state => state.stock.isApplied);
  const data = useSelector(state => state.stock.data);

  // КАТАЛОГ С ТОВАРАМИ (полные объекты)
  const catalogGoods = useSelector(state => state.catalog.products.list) || [];

  const { details = {} } = data || {};
  const { conditions = [], rewards = [] } = details || {};

  // -------------------- список "узлов" (conditions + rewards) --------------------

  const nodes = useMemo(() => {
    const conditionNodes = (conditions || []).map((c, idx) => {
      const hasGoods = Array.isArray(c.goods) && c.goods.length > 0;

      return {
        id: `condition-${idx}`,
        stockRole: 'conditions',
        index: idx,
        label: c.criteria || '',
        kind: 'condition',
        disabled: allConditionsChecked,
        hasGoods,
        applied_count: c?.applied_count,
        totalCount: c?.value,
      };
    });

    const rewardNodes = (rewards || []).map((r, idx) => {
      const hasGoods = Array.isArray(r.goods) && r.goods.length > 0;

      return {
        id: `reward-${idx}`,
        stockRole: 'rewards',
        index: idx,
        label: r.message || '',
        kind: 'reward',
        disabled: !allConditionsChecked, // призы недоступны, если условия не выполнены
        hasGoods,
      };
    });

    return [...conditionNodes, ...rewardNodes].filter(node => node.hasGoods);
  }, [conditions, rewards, allConditionsChecked]);

  // -------------------- текущий выбранный узел (радиобаттон) --------------------

  const [currentNodeId, setCurrentNodeId] = useState(() => {
    if (initialNodeId && nodes.some(n => n.id === initialNodeId && !n.disabled)) {
      return initialNodeId;
    }

    return nodes[0] ? nodes[0].id : null;
  });

  useEffect(() => {
    if (!initialNodeId) return;
    const exists = nodes.some(n => n.id === initialNodeId && !n.disabled);
    if (exists) {
      setCurrentNodeId(initialNodeId);
    }
  }, [initialNodeId, nodes]);

  const currentNode = useMemo(() => nodes.find(n => n.id === currentNodeId) || nodes[0] || null, [
    nodes,
    currentNodeId,
  ]);

  const setCurrentById = useCallback(
    id => {
      const node = nodes.find(n => n.id === id);
      if (!node || node.disabled) return;
      setCurrentNodeId(id);
    },
    [nodes],
  );

  // -------------------- гидрация товаров  --------------------

  const goodsById = useMemo(() => {
    const map = new Map();
    // eslint-disable-next-line no-restricted-syntax
    for (const g of catalogGoods) {
      map.set(Number(g.id), g);
    }
    return map;
  }, [catalogGoods]);

  const isFullGoodObject = good =>
    good &&
    typeof good === 'object' &&
    ('name' in good || (Array.isArray(good.variations) && typeof good.variations[0] === 'object'));

  const { title, hydratedGoods, type } = useMemo(() => {
    if (!currentNode) {
      return { title: '', hydratedGoods: [], type: 'good' };
    }

    const { stockRole, index } = currentNode;
    const isConditions = stockRole === 'conditions';

    const currentCondition = isConditions ? conditions[Number(index)] || {} : {};
    const currentReward = !isConditions ? rewards[Number(index)] || {} : {};

    const rawGoods = isConditions ? currentCondition.goods || [] : currentReward.goods || [];
    const titleLocal = isConditions ? currentCondition.criteria || '' : currentReward.message || '';
    const typeLocal = isConditions ? 'good' : 'gift';

    if (!Array.isArray(rawGoods) || rawGoods.length === 0) {
      return { title: titleLocal, hydratedGoods: [], type: typeLocal };
    }

    if (isFullGoodObject(rawGoods[0])) {
      return { title: titleLocal, hydratedGoods: rawGoods, type: typeLocal };
    }

    const hydrated = rawGoods
      .map(link => {
        const good = goodsById.get(Number(link.good_id));
        if (!good) return null;

        const requestedIds = Array.isArray(link.variations)
          ? new Set(link.variations.map(Number))
          : null;

        const filteredVariations = Array.isArray(good.variations)
          ? good.variations.filter(v => (requestedIds ? requestedIds.has(Number(v.id)) : true))
          : [];

        if (filteredVariations.length === 0) return null;

        return {
          ...good,
          variations: filteredVariations,
        };
      })
      .filter(Boolean);

    return { title: titleLocal, hydratedGoods: hydrated, type: typeLocal };
  }, [currentNode, conditions, rewards, goodsById]);

  const filtredGoods = useMemo(() => {
    return hydratedGoods.filter(
      good =>
        Array.isArray(good.variations) &&
        good.variations.some(variation => !variation.is_in_stop_list),
    );
  }, [hydratedGoods]);

  const removeIngredientAction = useCallback(
    ({ id, variation, ingredientId }) => {
      if (!currentNode) return null;
      const { stockRole, index } = currentNode;

      return dispatch(removeIngredient(id, variation.id, index, ingredientId, stockRole));
    },
    [dispatch, currentNode],
  );

  const cancelRemovedIngredientsAction = useCallback(
    ({ id, variation }) => {
      if (!currentNode) return null;
      const { stockRole, index } = currentNode;

      return dispatch(cancelRemovedIngredients(id, variation.id, index, stockRole));
    },
    [dispatch, currentNode],
  );

  const addToCart = useCallback(
    async variation => {
      if (!currentNode) return;
      const { stockRole, index } = currentNode;
      const isConditions = stockRole === 'conditions';

      if (isConditions) {
        await dispatch(addCartItem({ composition: [{ type: 'good', item: variation }] }));
        return;
      }

      const ingredients = variation.include_ingredients || [];
      const filtredIngredients =
        ingredients
          .filter(ingredient => ingredient.removed)
          .map(ingredient => ({ id: ingredient.id, count: 0 })) || [];

      const composition = [{ id: variation.id, ingredients: filtredIngredients }];

      await dispatch(addReward(index, composition, true));
      await onModalClose();
    },
    [currentNode, dispatch, isReplace, onModalClose],
  );

  return {
    isApplied,
    nodes,
    currentNode,
    setCurrentById,
    title,
    filtredGoods,
    removeIngredientAction,
    cancelRemovedIngredientsAction,
    addToCart,
    type,
  };
}
